import React, { useMemo } from 'react';

import {
  ActionIcon,
  Center,
  Divider,
  Grid,
  Group,
  Input,
  NumberInput,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { IconInfinity, IconTrash } from '@tabler/icons-react';
import EventTypeMultiSelectAutoComplete, {
  IEventTypeMultiSelectAutoComplete,
} from 'components/form-fields/auto-complete-fields/EventTypeMultiSelectAutoComplete';
import { computeEstimatedSessions } from 'utils/billing.helpers';

import EstimatedSessionsBadge from './EstimatedSessionsBadge';
import { CreditBasedPackageFormValues } from '../types';

interface ICreditBasedAttendanceRuleCardProps {
  disabled: boolean;
  index: number;
  form: UseFormReturnType<
    CreditBasedPackageFormValues,
    (values: CreditBasedPackageFormValues) => CreditBasedPackageFormValues
  >;
}

const CreditBasedAttendanceRuleCard = ({
  disabled,
  index,
  form,
}: ICreditBasedAttendanceRuleCardProps): JSX.Element => {
  const rule = form.values.attendanceRules[index];

  const estimatedSessions = useMemo(() => computeEstimatedSessions(rule, 'CREDIT_BASED'), [rule]);

  const errorMessages = Object.entries(form.errors)
    .filter(error => error[0].includes(`attendanceRules.${index}`))
    .map(error => error[1]);

  const handleEventTypesChange = (values: IEventTypeMultiSelectAutoComplete[]): void => {
    form.setFieldValue(
      `attendanceRules.${index}.kiloClassTypes`,
      values.filter(v => v.eventType === 'kilo_class_type'),
    );
    form.setFieldValue(
      `attendanceRules.${index}.appointmentTypes`,
      values.filter(v => v.eventType === 'appointment_type'),
    );
    const kiloClassTypeSet = new Set(
      values.filter(v => v.eventType === 'kilo_class_type').map(v => v.eventTypeId),
    );
    const appointmentTypeSet = new Set(
      values.filter(v => v.eventType === 'appointment_type').map(v => v.eventTypeId),
    );
    const oldKiloClassTypeSet = form.values.attendanceRules[index].oldKiloClassTypeSet;
    const oldAppointmentTypeSet = form.values.attendanceRules[index].oldAppointmentTypeSet;
    // @ts-ignore
    const classTypeIsSuperset = kiloClassTypeSet.isSupersetOf(oldKiloClassTypeSet);
    // @ts-ignore
    const appointmentTypeIsSuperset = appointmentTypeSet.isSupersetOf(oldAppointmentTypeSet);
    const appNotChanged =
      appointmentTypeIsSuperset && appointmentTypeSet.size === oldAppointmentTypeSet.size;
    const kiloNotChanged = classTypeIsSuperset && kiloClassTypeSet.size === oldKiloClassTypeSet.size;
    if (appNotChanged && kiloNotChanged) {
      form.setFieldValue(`attendanceRules.${index}.eventTypeState`, 'same');
    } else if (classTypeIsSuperset && appointmentTypeIsSuperset) {
      form.setFieldValue(`attendanceRules.${index}.eventTypeState`, 'superset');
    } else {
      form.setFieldValue(`attendanceRules.${index}.eventTypeState`, 'incompatible');
    }
  };

  const handleInfiniteSessions = (): void => {
    if (form.values.attendanceRules[index].credits !== null) {
      form.setFieldValue(`attendanceRules.${index}`, {
        ...form.values.attendanceRules[index],
        credits: null,
        interval: null,
        intervalCount: null,
        perOrPack: 'pack',
        isInfiniteSessions: true,
      });
    } else {
      form.setFieldValue(`attendanceRules.${index}`, {
        ...form.values.attendanceRules[index],
        credits: 1,
        interval: null,
        intervalCount: null,
        perOrPack: 'pack',
        isInfiniteSessions: false,
      });
    }
  };

  return (
    <Paper shadow='sm' withBorder radius='md' bg={disabled ? 'gray.1' : undefined}>
      <Group position='apart' bg='gray.2' p='xs'>
        {disabled ? (
          <Text weight='bold' size='sm' sx={{ flex: 1 }}>
            {form.values.attendanceRules[index].name}
          </Text>
        ) : (
          <EventTypeMultiSelectAutoComplete
            size='xs'
            sx={{ flex: 1 }}
            placeholder='Select Event Type'
            values={[
              ...form.values.attendanceRules[index].kiloClassTypes,
              ...form.values.attendanceRules[index].appointmentTypes,
            ]}
            handleChange={handleEventTypesChange}
          />
        )}
        <ActionIcon onClick={() => form.removeListItem('attendanceRules', index)} disabled={disabled}>
          <IconTrash size='1.2rem' />
        </ActionIcon>
      </Group>

      <Grid p={0} m={0} gutter={0} columns={14}>
        <Grid.Col span={8}>
          <Stack spacing='xs' p='sm'>
            <Input.Label required>Attendance</Input.Label>
            <Grid align='center' justify='center' gutter='xs' columns={29}>
              {!disabled && (
                <Grid.Col span={3}>
                  <ActionIcon
                    data-cy='infiniteSessionsButton'
                    color='kiloBlue'
                    variant={form.values.attendanceRules[index].isInfiniteSessions ? 'light' : 'outline'}
                    onClick={handleInfiniteSessions}
                    disabled={disabled}
                  >
                    <IconInfinity size='1rem' />
                  </ActionIcon>
                </Grid.Col>
              )}
              <Grid.Col span={5}>
                {form.values.attendanceRules[index].credits === null ? (
                  <TextInput value='&infin;' disabled size='xs' />
                ) : (
                  <NumberInput
                    size='xs'
                    min={1}
                    disabled={disabled}
                    {...form.getInputProps(`attendanceRules.${index}.credits`)}
                  />
                )}
              </Grid.Col>
              <Grid.Col span={5}>
                <Text size='xs'>Sessions</Text>
              </Grid.Col>
              <Grid.Col span={7}>
                <Select
                  {...form.getInputProps(`attendanceRules.${index}.perOrPack`)}
                  disabled={disabled || form.values.attendanceRules[index].isInfiniteSessions === true}
                  readOnly
                  size='xs'
                  data-cy='perOrPackSelect'
                  data={[{ value: 'pack', label: 'pack', 'data-cy': 'packOption' }]}
                />
              </Grid.Col>
              <Grid.Col span={5}></Grid.Col>
              <Grid.Col span={4}></Grid.Col>
            </Grid>
          </Stack>
        </Grid.Col>
        <Grid.Col span={3}></Grid.Col>
        <Grid.Col span={3}></Grid.Col>
      </Grid>
      {errorMessages.length > 0 && (
        <>
          <Divider />
          <Stack py='sm' align='center'>
            {errorMessages.map(msg => (
              <Center key={`msg-${msg}`}>
                <Text size='xs' c='red'>
                  {msg}
                </Text>
              </Center>
            ))}
          </Stack>
        </>
      )}
      <EstimatedSessionsBadge result={estimatedSessions} />
    </Paper>
  );
};

export default CreditBasedAttendanceRuleCard;
